// ../../Appointmint.Models/Client/InvoiceData.cs
export interface InvoiceData {
    date: string;
    dueDate?: string;
    clientId?: string;
    type: InvoiceDocumentType;
    reference?: string;
    observations?: string;
    currencyCode?: string;
    branchId?: string;
    items: InvoiceItem[];
    globalDiscountPercentage?: number;
    globalDiscountAmount?: number;
    payments?: Payment[];
    clientName?: string;
    address?: string;
    city?: string;
    country?: string;
    clientTaxNumber?: string;
    sendEmail?: boolean;
    appointmentId?: string;
    packId?: string;
}

// ../../Appointmint.Models/Client/InvoiceItem.cs
export interface InvoiceItem {
    code: string;
    description: string;
    unitPrice: number;
    quantity: number;
    unit?: string;
    discountPercentage?: number;
    taxId?: string;
    typeId?: InvoiceItemType;
    taxExemption?: string;
    taxExemptionLaw?: string;
}

// ../../Appointmint.Models/Client/UpdateTaskItemData.cs
export interface UpdateTaskItemData {
    clientId?: string;
    title?: string;
    dueDate: string;
}

// ../../Appointmint.Models/Client/SelfSchedulingLayout.cs
export enum SelfSchedulingLayout {
    ServicesList = 0,
    ServicesDropdown = 1,
}

// ../../Appointmint.Models/Client/MfaMethodEnum.cs
export enum MfaMethodEnum {
    None,
    Sms,
    Email,
    Authenticator,
}

// ../../Appointmint.Models/Client/InvoiceEmailSending.cs
export interface InvoiceEmailSending {
    type: InvoiceDocumentType;
    clientId: string;
    date: string;
}

// ../../Appointmint.Models/Client/Notification.cs
export interface Notification {
    type: NotificationTypeEnum;
    date: string;
    description: string;
    value?: string;
    entityId?: string;
    clientId?: string;
    clientName?: string;
    serviceType?: string;
}

// ../../Appointmint.Models/Client/SendCommunicationRequest.cs
export interface SendCommunicationRequest {
    channel: CommunicationType;
    message?: string;
    subject?: string;
    conversationId?: string;
    observations?: string;
}

// ../../Appointmint.Models/Client/GoogleAuthRequest.cs
export interface GoogleAuthRequest {
    idToken: string;
    companyId?: number;
    isClient?: boolean;
}

// ../../Appointmint.Models/Client/UserPreferencesData.cs
export interface UserPreferencesData {
    homeCalendar?: HomeCalendarPreferencesData;
    homeCalendarByBranch: Record<string, HomeCalendarPreferencesData>;
}

// ../../Appointmint.Models/Client/UserPreferencesData.cs
export interface HomeCalendarPreferencesData {
    columnType?: string;
    tooltipFields: string[];
    colors?: string;
    slotGranularity?: number;
    columnWidth?: number;
    roomColumnOrder: string[];
    professionalColumnOrder: string[];
    columnWidthByView: Record<string, number>;
}

// ../../Appointmint.Models/Client/WaitingListPriority.cs
export enum WaitingListPriority {
    Normal,
    High,
    Urgent,
}

// ../../Appointmint.Models/Client/FixedClientPropertySettings.cs
export interface FixedClientPropertySettings {
    hidden?: boolean;
    clientCanView?: boolean;
    clientCanEdit?: boolean;
}

// ../../Appointmint.Models/Client/AppointmentChangeReason.cs
export interface AppointmentChangeReason {
    reasonOrigin: AppointmentChangeOrigin;
    reason: string;
    reasonDetails?: string;
}

// ../../Appointmint.Models/Client/ClientSearch.cs
export interface ClientSearch {
    offset?: number;
    pageSize?: number;
    packTemplateId?: string;
    packExpiringInDays?: number;
    packNotPresent?: boolean;
    clientTags?: string[];
}

// ../../Appointmint.Models/Client/SystemNote.cs
export interface SystemNote {
    type: SystemNoteType;
    title: string;
    text: string;
    imageUrl: string;
}

// ../../Appointmint.Models/Client/SystemNote.cs
export enum SystemNoteType {
    Information,
    Warning,
    Critical,
}

// ../../Appointmint.Models/Client/IntegrationAppointmintInvoices.cs
export interface IntegrationAppointmintInvoices {
    autoridadeTributariaCreds: AutoridadeTributariaCreds;
}

// ../../Appointmint.Models/Client/ReportsSettingsData.cs
export interface ReportsSettingsData {
    documents: DocumentSettings[];
}

// ../../Appointmint.Models/Client/BirthdayMessageSettingsData.cs
export interface BirthdayMessageSettingsData {
    enabled?: boolean;
    includeClientTags?: string[];
    excludeClientTags?: string[];
    sendAtTime?: string;
    smsEnabled?: boolean;
    smsText?: string;
    whatsAppEnabled?: boolean;
    whatsAppCustomFarewell?: string;
    emailEnabled?: boolean;
    emailName?: string;
    emailSubject?: string;
    emailText?: string;
}

// ../../Appointmint.Models/Client/WhatsAppTemplateCatalogItem.cs
export interface WhatsAppTemplateCatalogItem {
    id: string;
    providerTemplateId: string;
    providerTemplateName: string;
    displayName: string;
    language: string;
    category: string;
    status: string;
    rejectedReason?: string;
    allowedUseCases: string[];
    version: number;
    componentsSnapshot: WhatsAppTemplateComponent[];
    createdBy: string;
    submittedAt?: string;
    approvedAt?: string;
    archivedAt?: string;
}

// ../../Appointmint.Models/Client/TaxRate.cs
export interface TaxRate {
    id: string;
    rate: number;
    country: string;
    title: string;
}

// ../../Appointmint.Models/Client/ResetPasswordData.cs
export interface ResetPasswordData {
    username: string;
    isClient: boolean;
}

// ../../Appointmint.Models/Client/ConsentsExtensions.cs
/**
 * Extension methods for the Consents class to provide reusable helper functions
 */
export interface ConsentsExtensions {
}

// ../../Appointmint.Models/Client/UpdateCommunicationObservationsRequest.cs
export interface UpdateCommunicationObservationsRequest {
    observations?: string;
}

// ../../Appointmint.Models/Client/AppointmentChangeRecordData.cs
export interface AppointmentChangeRecordData {
    id: string;
    timestamp: string;
    username: string;
    appointmentId: string;
    appointmentDate: string;
    appointmentType: string;
    branchId?: string;
    professionalId?: string;
    professionalName?: string;
    clientId?: string;
    clientName?: string;
    reasonOrigin: AppointmentChangeOrigin;
    reason?: string;
    reasonDetails?: string;
    changeType: AppointmentChangeType;
    newDate?: string;
    newClientId?: string;
    newClientName?: string;
}

// ../../Appointmint.Models/Client/StartCheckout.cs
export interface StartCheckout {
    easypayCheckoutId: string;
    easypayCheckoutSession: string;
    testMode: boolean;
}

// ../../Appointmint.Models/Client/StartCheckout.cs
export interface StartCheckoutOptions {
    paymentMethods?: PaymentMethodType[];
}

// ../../Appointmint.Models/Client/StartCheckout.cs
export enum PaymentMethodType {
    MBWay,
    RefMB,
    CreditCard,
    DigitalIBAN,
    DirectDebit,
    GooglePay,
    ApplePay,
    SamsungWallet,
}

// ../../Appointmint.Models/Client/CampaignStatus.cs
export enum CampaignStatus {
    Draft = 0,
    Ready = 1,
    AwaitingPayment = 2,
    Paid = 3,
    Scheduled = 4,
    Sending = 5,
    Sent = 6,
    PartiallyFailed = 7,
    Failed = 8,
    Archived = 9,
}

// ../../Appointmint.Models/Client/ClientData.cs
export interface ClientData {
    id?: string;
    identificationNumber?: string;
    name: string;
    taxNumber?: string;
    healthNumber?: string;
    subsystemName?: string;
    subsystemNumber?: string;
    sex?: SexEnum;
    birthday?: string;
    job?: string;
    origin?: string;
    addressLine1?: string;
    addressLine2?: string;
    zipCode?: string;
    city?: string;
    height?: number;
    photo?: string;
    phoneNumbers?: string[];
    email?: string;
    acceptMarktingCommunication?: boolean;
    warningNote?: string;
    notes?: string;
    internalNotes?: string;
    rgpdApproved?: boolean;
    consentResult?: ConsentResultData;
    consentEmailSentAt?: string;
    consentSmsSentAt?: string;
    consentWhatsAppSentAt?: string;
    status?: ClientStatus;
    tags?: string[];
    professionalId?: string;
    additionalProfessionalIds?: string[];
    visibleForAllProfessionals?: boolean;
    professionalAutomaticNotificationInDays?: number;
    properties?: Record<string, string>;
    clientFileNumber?: number;
    hasPortalAccess?: boolean;
    professionalNextNotification?: string;
    professionalNotesHistory?: Record<string, string>;
    mintuPausedUntil?: string;
}

// ../../Appointmint.Models/Client/InvoiceCatalogData.cs
export interface InvoiceCatalogData {
    defaultItems: InvoiceItem[];
}

// ../../Appointmint.Models/Client/InvoiceDocumentType.cs
export enum InvoiceDocumentType {
    Invoice,
    SimplifiedInvoice,
    InvoiceReceipt,
    CreditNote,
    Receipt,
}

// ../../Appointmint.Models/Client/SurveyResponse.cs
export interface SurveyResponse {
    id: string;
    surveyId: string;
    surveyName: string;
    clientId: string;
    appointmentId?: string;
    creationDate: string;
    values: Record<string, string>;
    isAnonymous: boolean;
    clientName?: string;
    professionalId?: string;
    professionalName?: string;
    serviceTypeId?: string;
    serviceTypeName?: string;
    roomId?: string;
    roomName?: string;
    branchId?: string;
    branchName?: string;
}

// ../../Appointmint.Models/Client/ProfessionalPaymentRow.cs
export interface ProfessionalPaymentRow extends ProfessionalPayment {
    nrHours: number;
    nrAppointments: number;
}

// ../../Appointmint.Models/Client/WhatsAppTemplateComponentParameters.cs
export interface WhatsAppTemplateComponentParameters {
    componentType: string;
    values: string[];
}

// ../../Appointmint.Models/Client/AdminStats.cs
export interface ThisAndLastMonthStat {
    lastMonth: number;
    thisMonth: number;
}

// ../../Appointmint.Models/Client/AdminStats.cs
export interface StorageStat {
    usedBytes: number;
    allowedBytes: number;
}

// ../../Appointmint.Models/Client/AdminStats.cs
export interface AiStats {
    inputTokensUsed: number;
    inputTokensAllowed: number;
    outputTokensUsed: number;
    outputTokensAllowed: number;
}

// ../../Appointmint.Models/Client/AdminStats.cs
export interface AdminStats {
    totalSmssSent: ThisAndLastMonthStat;
    totalEmailsSent: ThisAndLastMonthStat;
    totalWhatsAppsSent: ThisAndLastMonthStat;
    totalAppointments: ThisAndLastMonthStat;
    smsQuotaExhaustedOn?: string;
    emailsQuotaExhaustedOn?: string;
    whatsAppQuotaExhaustedOn?: string;
    allowedSmss: number;
    allowedEmails: number;
    allowedWhatsApps: number;
    allowedUsers: number;
    totalClients: number;
    totalUsers: number;
    totalProfessionals: number;
    documentsStorage: StorageStat;
    databaseStorage: StorageStat;
    aiStats?: AiStats;
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface ConsentManagementSettingsData {
    termsAndConditionsLink?: string;
    privacyPolicyLink?: string;
    consentsCheckboxes: ConsentsCheckboxSettingsData[];
    companyName?: string;
    companyLogo?: string;
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface ConsentsCheckboxSettingsData {
    name?: string;
    consents: Consents;
    isMandatory: boolean;
    text: string;
    links: Record<string, string>;
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface Consents {
    gdpr: boolean;
    healthDataProcessing: boolean;
    emailMarketing: boolean;
    smsMarketing: boolean;
    birthdayMessage: boolean;
    emailReminders: boolean;
    smsReminders: boolean;
    whatsAppReminders: boolean;
    dataSharingWithProfessionals: boolean;
    thirdPartyDataSharing: boolean;
    imageOrVideoCaptureForInstitutionalPurposes: boolean;
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface ApprovedCustomCheckbox {
    text: string;
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface ConsentResultData {
    approvedDate?: string;
    approvedConsents: Consents;
    approvedCustomCheckboxes: ApprovedCustomCheckbox[];
}

// ../../Appointmint.Models/Client/ConsentManagementSettingsData.cs
export interface ApproveConsentsRequest {
    approvedConsents: Consents;
    approvedCustomCheckboxTexts: string[];
}

// ../../Appointmint.Models/Client/ProfessionalData.cs
export interface ProfessionalData {
    id: string;
    name: string;
    email?: string;
    photoFileId?: string;
    photoUrl?: string;
    biography?: string;
    registrationNumber?: string;
    phoneNumbers?: string[];
    capabilities?: string[];
    timeAvailability?: ProfessionalAvailability[];
    absences?: DateInterval[];
    availabilityExtra?: DateInterval[];
    branchTimeAvailability?: Record<string, ProfessionalAvailability[]>;
    branchAbsences?: Record<string, DateInterval[]>;
    branchAvailabilityExtra?: Record<string, DateInterval[]>;
    colorCode?: string;
    /**
     * @deprecated
     */
    paymentsByCapability?: Record<string, ProfessionalPayment>;
    paymentByCapability?: Record<string, ProfessionalPayment[]>;
    ordemProfissional?: string;
    pemEspecialidadeCodigo?: string;
}

// ../../Appointmint.Models/Client/SurveyHistoryItem.cs
export interface SurveyHistoryItem {
    id: string;
    surveyId: string;
    surveyName: string;
    appointmentId?: string;
    sentAt: string;
    submittedAt?: string;
    isSubmitted: boolean;
    isAnonymousSubmission: boolean;
    values: Record<string, string>;
    professionalId?: string;
    professionalName?: string;
    serviceTypeId?: string;
    serviceTypeName?: string;
    roomId?: string;
    roomName?: string;
    branchId?: string;
    branchName?: string;
}

// ../../Appointmint.Models/Client/IntegrationMailerLiteSettings.cs
export interface IntegrationMailerLiteSettings {
    apiKey: string;
    fieldMappings?: MailerLiteFieldMappingData[];
    lastSubscribersImportAt?: string;
}

// ../../Appointmint.Models/Client/ShareOptions.cs
export interface ShareOptions {
    shareWithClient?: boolean;
    shareWithProfessionalsWithService?: string[] | null;
    shareWithAdministratives?: boolean;
    sharedWithUserIds?: string[];
}

// ../../Appointmint.Models/Client/SurveyTokenData.cs
export interface SurveyTokenData {
    survey: SurveySettingsData;
    clientName: string;
    companyName: string;
    companyImage?: string;
    appointmentDate: string;
    appointmentTime: string;
    professionalName: string;
    appointmentType: string;
    alreadyResponded: boolean;
    existingValues?: Record<string, string>;
    isPreview: boolean;
}

// ../../Appointmint.Models/Client/AppointmentResult.cs
export enum AppointmentResult {
    Success,
    OverlapOnClient,
    OverlapOnProfessional,
    OverlapOnRoom,
    ProfessionalNotAvailable,
    ProfessionalAbsent,
    BatchCreationDoesNotSupportPacks,
    PackDoesntSupportAppoinmentType,
    PackNotFound,
    PackIsFull,
    PackExpired,
    ConflictPaymentsAndPack,
    BelongsToOtherProfessional,
    CantChangePast,
    InvalidData,
    CannotChangeServiceTypeWithUncanceledInvoice,
    Holiday,
    MultipleClientsFound,
}

// ../../Appointmint.Models/Client/WaitingListSettingsData.cs
export interface WaitingListSettingsData {
    removeFromWaitingListWhenAppointmentCreated: boolean;
}

// ../../Appointmint.Models/Client/LoggedUser.cs
export interface LoggedUser {
    isClient: boolean;
    token: string;
    username?: string;
    features: FeatureEnum[];
    permissions?: PermissionEnum[];
    accountExpiryDate: string;
    storageIsFull: boolean;
    isPaymentExpired: boolean;
    hasMultipleRoles?: boolean;
    needsOnboarding?: boolean;
    businesses: BusinessInfo[];
    systemNotes: SystemNote[];
    defaultCashRegisterName?: string;
    branchIds?: string[];
}

// ../../Appointmint.Models/Client/SendConsentApprovalResult.cs
export interface SendConsentApprovalResult {
    channel: string;
    sentAt: string;
}

// ../../Appointmint.Models/Client/ServicePrePaymentSettings.cs
export interface ServicePrePaymentSettings {
    enabled: boolean;
    applicableServices?: string[];
    autoDeleteIfNotPaid?: boolean;
    autoDeleteDaysAfterCreation?: number;
    autoDeleteDaysBeforeAppointment?: number;
    autoDeleteIfNotPaidOnDayOfMonth?: number;
    autoDeleteGroups?: ServiceAutoDeleteGroupSettings[];
    paymentReminder?: ServicePaymentReminderSettings;
    paymentConfirmed?: ServicePaymentEventSettings;
    paymentCancelled?: ServicePaymentEventSettings;
}

// ../../Appointmint.Models/Client/ServicePrePaymentSettings.cs
export interface ServiceAutoDeleteGroupSettings {
    applicableServices: string[];
    autoDeleteIfNotPaid?: boolean;
    autoDeleteDaysAfterCreation?: number;
    autoDeleteDaysBeforeAppointment?: number;
    autoDeleteIfNotPaidOnDayOfMonth?: number;
}

// ../../Appointmint.Models/Client/ServicePrePaymentSettings.cs
export interface ServicePaymentReminderSettings {
    smsText?: string;
    smsOnAppointmentCreation?: boolean;
    smsHoursBeforeAppointment: number[];
    whatsAppText?: string;
    whatsAppOnAppointmentCreation?: boolean;
    whatsAppHoursBeforeAppointment: number[];
    emailName?: string;
    emailSubject?: string;
    emailText?: string;
    emailOnAppointmentCreation?: boolean;
    emailHoursBeforeAppointment: number[];
}

// ../../Appointmint.Models/Client/ServicePrePaymentSettings.cs
export interface ServicePaymentEventSettings {
    smsEnabled?: boolean;
    smsText?: string;
    whatsAppEnabled?: boolean;
    emailEnabled?: boolean;
    emailName?: string;
    emailSubject?: string;
    emailText?: string;
}

// ../../Appointmint.Models/Client/ConsentDeliveryChannel.cs
export enum ConsentDeliveryChannel {
    Email,
    WhatsApp,
    SMS,
}

// ../../Appointmint.Models/Client/InvoiceEmissionResult.cs
export interface InvoiceEmissionResult {
    isSuccess: boolean;
    invoice?: Invoice;
    errorTranslationKey?: TranslationKey;
    errorMessage?: string;
}

// ../../Appointmint.Models/Client/CampaignConsentMode.cs
export enum CampaignConsentMode {
    ApprovedOnly = 0,
    IncludeNotSubmitted = 1,
    IgnoreAll = 2,
}

// ../../Appointmint.Models/Client/BusinessInfo.cs
export interface BusinessInfo {
    id: string;
    name: string;
    logo?: string;
    usernames: string[];
}

// ../../Appointmint.Models/Client/SexEnum.cs
export enum SexEnum {
    Unspecified,
    Male,
    Female,
}

// ../../Appointmint.Models/Client/AssignTaskItemData.cs
export interface AssignTaskItemData {
    assignedUserId?: string;
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface WhatsAppTemplateComponent {
    type: string;
    format?: string;
    text?: string;
    buttons?: WhatsAppTemplateButton[];
    example?: WhatsAppTemplateExample;
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface WhatsAppTemplateButton {
    type: string;
    text: string;
    url?: string;
    phoneNumber?: string;
    example?: string;
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface WhatsAppTemplateExample {
    headerText?: string[];
    bodyText?: string[][];
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface WhatsAppMessageTemplate {
    id: string;
    name: string;
    category: string;
    language: string;
    status: string;
    rejectedReason?: string;
    components: WhatsAppTemplateComponent[];
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface WhatsAppMessageTemplateListResult {
    templates: WhatsAppMessageTemplate[];
    nextCursor?: string;
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface CreateWhatsAppTemplateRequest {
    name: string;
    category: string;
    language: string;
    components: WhatsAppTemplateComponent[];
}

// ../../Appointmint.Models/Client/WhatsAppMessageTemplate.cs
export interface UpdateWhatsAppTemplateRequest {
    components?: WhatsAppTemplateComponent[];
    category?: string;
}

// ../../Appointmint.Models/Client/SurveyAnalytics.cs
export interface SurveyAnalyticsDimensionMetric {
    id: string;
    label: string;
    totalResponses: number;
    average: number;
}

// ../../Appointmint.Models/Client/SurveyAnalytics.cs
export interface SurveyAnalyticsFieldResult {
    key: string;
    label: string;
    type: SurveyFieldType;
    average?: number;
    optionCounts?: Record<string, number>;
    textResponses?: string[];
    averageByProfessional: SurveyAnalyticsDimensionMetric[];
    averageByServiceType: SurveyAnalyticsDimensionMetric[];
    averageByRoom: SurveyAnalyticsDimensionMetric[];
}

// ../../Appointmint.Models/Client/SurveyAnalytics.cs
export interface SurveyAnalyticsResponse {
    id: string;
    creationDate: string;
    isAnonymous: boolean;
    clientName?: string;
    professionalName?: string;
    serviceTypeName?: string;
    roomName?: string;
    branchName?: string;
    values: Record<string, string>;
}

// ../../Appointmint.Models/Client/SurveyAnalytics.cs
export interface SurveyAnalyticsData {
    totalResponses: number;
    fields: SurveyAnalyticsFieldResult[];
    responses: SurveyAnalyticsResponse[];
}

// ../../Appointmint.Models/Client/RemindersSettingsData.cs
export interface RemindersSettingsData {
    servicesTypesKeys?: string[];
    smsFrom?: string;
    smsText?: string;
    smsHoursBeforeAppointment?: number[];
    smsOnAppointmentCreation?: boolean;
    whatsAppText?: string;
    whatsAppHoursBeforeAppointment?: number[];
    whatsAppOnAppointmentCreation?: boolean;
    whatsAppConfirmationMessage?: string;
    emailName?: string;
    emailFrom?: string;
    emailText?: string;
    emailSubject?: string;
    emailHoursBeforeAppointment?: number[];
    emailOnAppointmentCreation?: boolean;
    appointmentConfirmation?: AppointmentConfirmationSettingsData;
    clientsFollowUp?: ClientsFollowUpSettingsData;
    birthdayMessage?: BirthdayMessageSettingsData;
}

// ../../Appointmint.Models/Client/TranslationKey.cs
export interface TranslationKey {
    key: string;
    default: string;
}

// ../../Appointmint.Models/Client/PacksSettingsData.cs
export interface PacksSettingsData {
    packsConfig: PackSettings[];
}

// ../../Appointmint.Models/Client/CampaignProcess.cs
export interface CampaignProcessItem {
    email: string;
    clientId?: string;
    clientName?: string;
    additionalInformation?: string;
    status: CampaignProcessStatus;
    statusTimestamp: string;
    failureReason?: string;
    providerMessageId?: string;
    providerStatus?: string;
    acceptedAt?: string;
    sentAt?: string;
    deliveredAt?: string;
    openedAt?: string;
    clickedAt?: string;
    bouncedAt?: string;
    blockedAt?: string;
    spamReportedAt?: string;
    unsubscribedAt?: string;
    readAt?: string;
}

// ../../Appointmint.Models/Client/CampaignProcess.cs
export interface CampaignProcess {
    id: string;
    createdBy: string;
    creationDate: string;
    lastChangeDt: string;
    status: CampaignProcessStatus;
    items: CampaignProcessItem[];
    channel: CampaignChannel;
    startedAt?: string;
    completedAt?: string;
    recipientCount: number;
    sentCount: number;
    failedCount: number;
}

// ../../Appointmint.Models/Client/ReportFieldDefinition.cs
export interface ReportFieldDefinition {
    label: string;
    type: ReportFieldType;
    isMandatory?: boolean;
    description?: string;
    defaultValue?: string;
    helpNotes?: string;
    fontSize?: number;
    height?: number;
    flags?: ReportFieldFlag[];
    aditionalFields?: Record<string, string>;
}

// ../../Appointmint.Models/Client/CegidVendusBranchStoreSettings.cs
export interface CegidVendusBranchStoreSettings {
    branchId: string;
    storeId: number;
    registerId: number;
}

// ../../Appointmint.Models/Client/CampaignBase.cs
export interface CampaignBase {
    name: string;
    subject?: string;
    content?: string;
    creationDate?: string;
    lastUpdateDt?: string;
    recipientsAllClients?: boolean;
    ignoreMarketingConsent?: boolean;
    recipientsClientsTagsToInclude?: string[];
    recipientsClientsIdsToInclude?: string[];
    recipientsClientsIdsToIgnore?: string[];
    recipientsAdditionalEmails?: string[];
    createdBy?: string;
    channel: CampaignChannel;
    goal?: CampaignGoal;
    consentMode: CampaignConsentMode;
    internalDescription?: string;
    preheader?: string;
    senderName?: string;
    senderEmail?: string;
    builderStep: number;
    includeClientsWithoutMarketingConsentSubmitted: boolean;
    templateId?: string;
    templateVersion?: string;
    templateLanguage?: string;
    templateParameters?: WhatsAppTemplateComponentParameters[];
}

// ../../Appointmint.Models/Client/AppointmentData.cs
export interface AppointmentData {
    id: string;
    clientId?: string;
    professionalId: string;
    type: string;
    room?: string;
    date: string;
    start: string;
    end: string;
    branchId?: string;
    price: number;
    vat: number;
    notes?: string;
    hasHappened?: boolean;
    confirmedByClientAt?: string;
    deniedByClientAt?: string;
    confirmationRequestsSentBySms: number[];
    confirmationRequestsSentByWhatsapp: number[];
    packId?: string;
    payments: Payment[];
    invoices?: Invoice[];
    expenses: ExtraItem[];
    clientName?: string;
    clientFileNumber?: number;
    professionalName?: string;
    professionalColor?: string;
    packName?: string;
    actualPriceWithPack?: number;
    companyId?: string;
    clientRequest?: ClientRequestType;
    additionalProfessionals?: ProfessionalRef[];
    packTotalMinutes?: number;
    packTotalCredits?: number;
    packAppointmentPosition?: number;
    recurring?: RecurringInfo;
    paymentLink?: string;
    creationDate: string;
    disableAutoCancellation?: boolean;
    groupClients?: GroupAppointmentClient[];
}

// ../../Appointmint.Models/Client/BranchProfessionalSettings.cs
export interface BranchProfessionalSettings {
    capabilities: string[];
    timeAvailability: Availability[];
    absences: DateInterval[];
    availabilityExtra: DateInterval[];
}

// ../../Appointmint.Models/Client/AnonymousScheduleRequest.cs
export interface AnonymousScheduleRequest {
    dateTime: string;
    appointmentType: string;
    room: string;
    professionalId: string;
    clientName: string;
    clientEmail: string;
    clientPhone: string;
    notes?: string;
    acceptPrivacyPolicy: boolean;
    clientId?: string;
}

// ../../Appointmint.Models/Client/ClientsLoginSettingsData.cs
export interface ClientsLoginSettingsData {
    enabled: boolean;
    selfScheduling?: SelfSchedulingSettingsData;
    mintu?: MintuSettingsData;
}

// ../../Appointmint.Models/Client/InvoiceSummaryData.cs
export interface InvoiceSummaryData {
    totalDocuments: number;
    totalInvoices: number;
    totalInvoiceReceipts: number;
    totalCreditNotes: number;
    totalReceipts: number;
    totalCanceled: number;
    grossInvoiced: number;
    netInvoiced: number;
    creditNotesTotal: number;
    outstandingTotal: number;
    daily: InvoiceSummaryDailyData[];
}

// ../../Appointmint.Models/Client/InvoiceSummaryData.cs
export interface InvoiceSummaryDailyData {
    date: string;
    invoices: number;
    creditNotes: number;
    receipts: number;
    grossInvoiced: number;
    creditNotesTotal: number;
}

// ../../Appointmint.Models/Client/IntegrationsSettingsData.cs
export interface IntegrationsSettingsData {
    webhook?: IntegrationWebhookSettings;
    goHighLevel?: IntegrationGoHighLevelSettings;
    mailerLite?: IntegrationMailerLiteSettings;
    google?: GoogleSettingsData;
}

// ../../Appointmint.Models/Client/AutoridadeTributariaCreds.cs
export interface AutoridadeTributariaCreds {
    atUser?: string;
    atPassword?: string;
    fiscalId?: string;
}

// ../../Appointmint.Models/Client/ChangeInvoiceState.cs
export interface ChangeInvoiceState {
    type: InvoiceDocumentType;
    newState: InvoiceState;
    branchId?: string;
    appointmentId?: string;
    packId?: string;
    message?: string;
}

// ../../Appointmint.Models/Client/TaskManagementSettingsData.cs
export interface TaskManagementSettingsData {
    enabled?: boolean;
    clientsFollowUp?: ClientsFollowUpSettingsData;
}

// ../../Appointmint.Models/Client/GoogleReviewSubmission.cs
export interface GoogleReviewSubmission {
    stars: number;
    comment?: string;
    improvementNote?: string;
}

// ../../Appointmint.Models/Client/InvoiceReduced.cs
export interface InvoiceReduced {
    id: string;
    number?: string;
    type: InvoiceDocumentType;
    state: InvoiceState;
    total: number;
    date: string;
    dueDate?: string;
    clientId?: string;
    clientName?: string;
    address?: string;
    city?: string;
    country?: string;
    clientTaxNumber?: string;
    reference?: string;
    observations?: string;
    currencyCode?: string;
    branchId?: string;
    appointmentId?: string;
    packId?: string;
    hasReceipt?: boolean;
}

// ../../Appointmint.Models/Client/ClientStatus.cs
export enum ClientStatus {
    None = 0,
    Active,
    Inactive,
}

// ../../Appointmint.Models/Client/ServiceSettings.cs
export interface ServiceSettings {
    defaultPrice: number;
    defaultDurationMinutes: number;
    defaultRooms: string[];
    name?: string;
    category?: string;
    description?: string;
    color?: string;
    serviceType?: ServiceType;
    defaultPriceByOrigin: Record<string, number>;
    stockItems: StockItemUsage[];
    invoiceItemDetails?: InvoiceItemDetails;
    sendInvoiceBeforeAppointment?: boolean;
    sendInvoiceDaysBeforeAppointment?: number;
    sendReceiptOnPayment?: boolean;
    sendReceiptOnAllPaymentMethods?: boolean;
    sendReceiptOnPaymentMethods: string[];
    sendReceiptOnOnlinePayments?: boolean;
}

// ../../Appointmint.Models/Client/SelfSchedulingSettingsData.cs
export interface SelfSchedulingSettingsData {
    enabled: boolean;
    layout: SelfSchedulingLayout;
    allowAnonymous: boolean;
    allowAllByDefault?: boolean;
    privacyPolicyUrl?: string;
    professionalsPerAppointmentType: Record<string, string[]>;
    preventCalendarHoles: boolean;
    maximumDaysToSchedule: number;
    minimumDaysToSchedule: number;
    minimumDaysToAutoApproveSchedule: number;
    minimumDaysToCancel: number;
    minimumDaysToAutoApproveCancel: number;
    defaultRooms: string[];
    sendEmailWhenClientSelfSchedulesAndNotApprovedYet: boolean;
    pendingApprovalEmailCustomMessage?: string;
    allowShareDocumentsWithClients?: boolean;
}

// ../../Appointmint.Models/Client/MailerLiteAvailableFieldData.cs
export interface MailerLiteAvailableFieldData {
    id: string;
    name: string;
    key: string;
    type: string;
}

// ../../Appointmint.Models/Client/AppointmentChangeOrigin.cs
export enum AppointmentChangeOrigin {
    None = 0,
    Professional = 1,
    Client = 2,
    Organization = 3,
    Other = 4,
}

// ../../Appointmint.Models/Client/InvoiceItemDetails.cs
export interface InvoiceItemDetails {
    customTitle?: string;
    customDescription?: string;
    type: InvoiceItemType;
    taxId?: string;
    taxExemption?: string;
    taxExemptionLaw?: string;
}

// ../../Appointmint.Models/Client/InvoiceItemDetails.cs
export enum InvoiceItemType {
    P,
    S,
    O,
    I,
    E,
}

// ../../Appointmint.Models/Client/IntegrationWebhookSettings.cs
export interface IntegrationWebhookSettings {
    url: string;
    secret?: string;
    events: string[];
    isActive: boolean;
    synchronous: boolean;
    headers: Record<string, string>;
}

// ../../Appointmint.Models/Client/UpdateUserResult.cs
export enum UpdateUserResult {
    Updated,
    WeakPassword,
}

// ../../Appointmint.Models/Client/CommunicationAttachmentUpload.cs
export interface CommunicationAttachmentUpload {
    fileName: string;
    contentType: string;
    content: number[];
    length: number;
}

// ../../Appointmint.Models/Client/ChangeKind.cs
export enum ChangeKind {
    Created,
    Deleted,
    Updated,
}

// ../../Appointmint.Models/Client/ReportSettings.cs
export interface ReportSettings {
    id: string;
    name: string;
    version: number;
    disabled?: boolean;
    icon?: string;
    colorCode?: string;
    hideHeader?: boolean;
    availableForAllAppointmentTypes?: boolean;
    editableAfterCreationMinutes?: number;
    creationDate: string;
    availableForAppointmentTypes: string[];
    mandatoryForAppointmentTypes: string[];
    availableForAppointmentsManager: boolean;
    shareWithClient?: boolean;
    fields: ReportFieldDefinition[];
    showReportInline?: boolean;
    printZoom?: number;
}

// ../../Appointmint.Models/Client/AlertsSettingsData.cs
export interface AlertsSettingsData {
    professional?: ProfessionalAlerts;
}

// ../../Appointmint.Models/Client/AlertsSettingsData.cs
export interface ProfessionalAlerts {
    appointmentReminderMinutesBefore?: number;
    appointmentCreatedOnNextHours?: number;
    appointmentChangedOnNextHours?: number;
    appointmentCancelledOnNextHours?: number;
    nextDaySummarySendingTime?: string;
    nextDaySummaryForProfessionals?: string[];
}

// ../../Appointmint.Models/Client/NotificationTypeEnum.cs
export enum NotificationTypeEnum {
    AppointmentMissingPayment = 0,
    SmsReceived = 2,
    ClientBirthday = 3,
    PackMissingPayment = 4,
    ClientRequest = 5,
    ClientInactive = 6,
    ClientFollowUp = 7,
    OrderMissingPayment = 8,
}

// ../../Appointmint.Models/Client/CreateUserResultEnum.cs
export enum CreateUserResultEnum {
    Created,
    WeakPassword,
    UsernameInUse,
    CompanyNameInUse,
    InvalidUsername,
    MaxUsersReached,
    TermsNotAccepted,
    PrivacyNotAcknowledged,
}

// ../../Appointmint.Models/Client/ServiceType.cs
export enum ServiceType {
    ClientAppointment,
    GroupAppointment,
}

// ../../Appointmint.Models/Client/InvoiceExtensions.cs
export interface InvoiceExtensions {
}

// ../../Appointmint.Models/Client/DayOfWeekEnum.cs
export enum DayOfWeekEnum {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6,
    Holiday = 7,
}

// ../../Appointmint.Models/Client/GroupAppointmentClient.cs
export interface GroupAppointmentClient {
    clientId?: string;
    clientName?: string;
    attended?: boolean;
}

// ../../Appointmint.Models/Client/ClientTagRuleSettings.cs
export interface ClientTagRuleSettings {
    ruleId: string;
    enabled: boolean;
    tagValue: string;
    color: string;
    type: ClientTagRuleType;
    days: number;
}

// ../../Appointmint.Models/Client/CampaignGoal.cs
export enum CampaignGoal {
    Custom = 0,
    ReactivateClients = 1,
    PromoteService = 2,
    AnnounceNews = 3,
    BirthdayOrSeasonal = 4,
}

// ../../Appointmint.Models/Client/MfaRequiredResponse.cs
export interface MfaRequiredResponse {
    mfaRequired: boolean;
    sessionId: string;
    mfaMethod: MfaMethodEnum;
    maskedPhoneNumber?: string;
}

// ../../Appointmint.Models/Client/RoomSettings.cs
export interface RoomSettings {
    maxCapacity: number;
    timeAvailability: ProfessionalAvailability[];
}

// ../../Appointmint.Models/Client/FeatureEnum.cs
export enum FeatureEnum {
    None = 0,
    Billing,
    Reminders,
    UNUSED,
    Reports,
    Packs,
    ClientsLogin,
    AutomaticPayments,
    AdminPanel,
    CRM,
    ProfessionalPacks,
    Analytics,
    Invoices,
    ArtificialInteligence,
    ThirdPartyIntegrations,
    Stock,
    Branches,
    SupportChat,
    ConsentManagement,
    Surveys,
    Prescriptions,
    AiAppointmentsScriber,
}

// ../../Appointmint.Models/Client/StockHistoryData.cs
export interface StockHistoryData {
    id: string;
    stockItemId: string;
    quantity: number;
    type: StockHistoryType;
    notes: string;
    appointmentId?: string;
    extraId?: string;
    expenseId?: string;
    username?: string;
    date: string;
}

// ../../Appointmint.Models/Client/GoogleReviewTokenData.cs
export interface GoogleReviewTokenData {
    clientName: string;
    companyName: string;
    companyImage?: string;
    googleReviewUrl?: string;
    alreadySubmitted: boolean;
}

// ../../Appointmint.Models/Client/ExpenseData.cs
export interface FileUploadInfo {
    stream: Blob;
    fileName: string;
    contentType: string;
    fileSize: number;
}

// ../../Appointmint.Models/Client/ExpenseData.cs
export interface ExpenseAttachment {
    fileId: string;
    fileName: string;
    contentType: string;
    url?: string;
}

// ../../Appointmint.Models/Client/ExpenseData.cs
export interface ExpenseData {
    id: string;
    name: string;
    notes?: string;
    type: string;
    entity: string;
    professionalId?: string;
    professionalName?: string;
    recurring?: RecurringConfig;
    payment: Payment;
    attachments: ExpenseAttachment[];
    createdBy?: string;
}

// ../../Appointmint.Models/Client/ExpenseData.cs
export interface RecurringConfig {
    endDate?: string;
    unit: RecurringUnit;
    value: number;
}

// ../../Appointmint.Models/Client/ExpenseData.cs
export enum RecurringUnit {
    Days,
    Weeks,
    Months,
}

// ../../Appointmint.Models/Client/IntegrationGoHighLevelSettings.cs
export interface IntegrationGoHighLevelSettings {
    privateIntegrationKey: string;
    locationId: string;
    lastFullSync?: string;
    lastSync?: string;
    totalClientsSync?: number;
    totalAppointmentsSync?: number;
    createCalendarPerServiceType?: boolean;
    associateProfessionalsWithGhlUsers?: boolean;
    /**
     * Maps Appointmint ProfessionalId → GoHighLevel UserId
     */
    professionalUserMappings?: Record<string, string>;
}

// ../../Appointmint.Models/Client/MintuSettingsData.cs
export interface MintuSettingsData {
    customInstructions?: string;
}

// ../../Appointmint.Models/Client/PaymentProfile.cs
export interface PaymentProfile {
    id: string;
    name: string;
    version: number;
    creationDate: string;
    type: PaymentProfileType;
    table: ProfessionalPaymentRow[];
}

// ../../Appointmint.Models/Client/ClientRequestType.cs
export enum ClientRequestType {
    None,
    Create,
    Cancel,
    Reschedule,
    Approved,
}

// ../../Appointmint.Models/Client/CompanyLegalInfo.cs
export interface CompanyLegalInfo {
    address?: string;
    postalCode?: string;
    city?: string;
    taxId?: string;
    taxExemption?: string;
    taxExemptionLaw?: string;
}

// ../../Appointmint.Models/Client/ClientIntelligentCategorizationSettings.cs
export interface ClientIntelligentCategorizationSettings {
    rules: ClientTagRuleSettings[];
    lastRunDateUtc?: string;
}

// ../../Appointmint.Models/Client/InvoiceSettingsData.cs
export interface InvoiceSettingsData {
    appointmintInvoices?: IntegrationAppointmintInvoices;
    invoiceXpress?: IntegrationInvoiceXpress;
    cegidVendus?: IntegrationCegidVendus;
    cegidVendusSimulated?: IntegrationCegidVendusSimulated;
    provider?: InvoicesProvider;
    activatedAt?: string;
    sendInvoicesAutomaticallyToClientsByEmail?: boolean;
    sendSaftReportAutomaticallyToAt?: boolean;
    saftReportRecipientEmails?: string;
    saftReportSendDay?: number;
    emitInvoiceAutomaticallyOnPaymentConfirmation?: boolean;
    addAppointmentPackDetailsAutomaticallyOnInvoice?: boolean;
    appointmentPackDetailsTemplate?: string;
    generalObservations?: string;
    defaultPaymentMethod?: string;
    companyLegalInfo: CompanyLegalInfo;
    branchesLegalInfo?: Record<string, CompanyLegalInfo>;
}

// ../../Appointmint.Models/Client/IntegrationCegidVendus.cs
export interface IntegrationCegidVendus {
    apiKey?: string;
    storeId?: number;
    registerId?: number;
    branchesStoresSettings?: CegidVendusBranchStoreSettings[];
}

// ../../Appointmint.Models/Client/GoogleReviewSummary.cs
export interface GoogleReviewSummary {
    sentAt: string;
    openedAt?: string;
    submittedAt?: string;
    clickedGoogleAt?: string;
    channel: CommunicationType;
    stars?: number;
    state: GoogleReviewState;
}

// ../../Appointmint.Models/Client/GoogleReviewSummary.cs
export enum GoogleReviewState {
    NotSent = 0,
    Pending = 1,
    Submitted = 2,
}

// ../../Appointmint.Models/Client/GoogleReviewSettingsData.cs
export interface GoogleReviewSettingsData {
    smsTemplate?: string;
    emailBodyText?: string;
}

// ../../Appointmint.Models/Client/InvoiceState.cs
export enum InvoiceState {
    None = 0,
    Draft,
    Issued,
    Canceled = 4,
}

// ../../Appointmint.Models/Client/IntegrationSyncResult.cs
export interface IntegrationSyncResult {
    success: boolean;
    message?: string;
}

// ../../Appointmint.Models/Client/CampaignAnalytics.cs
export interface CampaignAnalytics {
    campaignId: string;
    campaignName: string;
    status: CampaignStatus;
    channel: CampaignChannel;
    goal?: CampaignGoal;
    totalRecipients: number;
    totalSent: number;
    totalFailed: number;
    totalPending: number;
    totalDelivered: number;
    totalOpened: number;
    totalClicked: number;
    totalRead: number;
    totalReplied?: number;
    lastTriggeredAt?: string;
    lastTriggeredBy?: string;
    isReTriggerEligible: boolean;
    reTriggerEligibleAt?: string;
    processes: CampaignProcessSummary[];
    recipientStatusRows: CampaignRecipientStatusRow[];
    failureReasonBreakdown: Record<string, number>;
    latestProcessDetails?: CampaignProcess;
}

// ../../Appointmint.Models/Client/CampaignAnalytics.cs
export interface CampaignProcessSummary {
    id: string;
    status: CampaignProcessStatus;
    channel: CampaignChannel;
    creationDate: string;
    startedAt?: string;
    completedAt?: string;
    createdBy: string;
    recipientCount: number;
    sentCount: number;
    failedCount: number;
    orderId?: string;
    orderTotal?: number;
    orderCurrency?: string;
}

// ../../Appointmint.Models/Client/CampaignAnalytics.cs
export interface CampaignRecipientStatusRow {
    email: string;
    clientName?: string;
    status: CampaignProcessStatus;
    readAt?: string;
    deliveredAt?: string;
    openedAt?: string;
    clickedAt?: string;
    failureReason?: string;
}

// ../../Appointmint.Models/Client/ClientFile.cs
export interface ClientFile {
    id: string;
    clientId: string;
    fileName: string;
    contentType?: string;
    url: string;
    createdBy: string;
    createdByName?: string;
    creationDate: string;
    documentSettings?: DocumentSettings;
    share?: ShareOptions;
}

// ../../Appointmint.Models/Client/PaymentProfileType.cs
export enum PaymentProfileType {
    None,
    Absolute,
    Steps,
}

// ../../Appointmint.Models/Client/WaitingListRemovalReason.cs
export enum WaitingListRemovalReason {
    Manual,
    AppointmentCreated,
}

// ../../Appointmint.Models/Client/CashRegisterEntryData.cs
export interface CashRegisterEntryData {
    id: string;
    paymentId?: string;
    paymentType: string;
    paymentValue: number;
    dateTime: string;
    username: string;
    appointmentId?: string;
    appointmentType?: string;
    packId?: string;
    packName?: string;
    orderId?: string;
    professionalId?: string;
    professionalName?: string;
    clientId?: string;
    clientName?: string;
    handledByUser?: string;
    handledAt?: string;
    description?: string;
    cashRegisterName?: string;
    transferId?: string;
    transferDirection?: CashRegisterTransferDirection;
    transferCounterpartyName?: string;
    transferCreatedByUser?: string;
    createdByUser?: string;
}

// ../../Appointmint.Models/Client/ProfessionalRef.cs
export interface ProfessionalRef {
    id: string;
    name: string;
    colorCode?: string;
}

// ../../Appointmint.Models/Client/CampaignProcessStatus.cs
export enum CampaignProcessStatus {
    Unknown = 0,
    Created,
    Sending,
    Done,
    Cancelled,
    Error,
}

// ../../Appointmint.Models/Client/InvoicesProvider.cs
export enum InvoicesProvider {
    InvoiceXpress = 0,
    CegidVendus = 1,
    Appointmint = 2,
    Simulated = 3,
}

// ../../Appointmint.Models/Client/PhysicalConsentUploadRequest.cs
/**
 * Request payload for uploading a physical consent document (multipart/form-data).
 * The IFormFile is bound separately in the controller.
 */
export interface PhysicalConsentUploadRequest {
    approvedConsents: Consents;
    approvedCustomCheckboxTexts: string[];
}

// ../../Appointmint.Models/Client/ClientReportsAndFiles.cs
export interface ClientReportsAndFiles {
    files: ClientFile[];
    reports: Report[];
    tasks: TaskItemData[];
    surveyHistory: SurveyHistoryItem[];
}

// ../../Appointmint.Models/Client/IntegrationCegidVendusSimulated.cs
export interface IntegrationCegidVendusSimulated {
    apiKey?: string;
    storeId?: number;
    registerId?: number;
    branchesStoresSettings?: CegidVendusBranchStoreSettings[];
}

// ../../Appointmint.Models/Client/CashRegisterTransferDirection.cs
export enum CashRegisterTransferDirection {
    Out,
    In,
}

// ../../Appointmint.Models/Client/IntegrationType.cs
export enum IntegrationType {
    GoHighLevel,
    MailerLite,
    InvoiceXpress,
}

// ../../Appointmint.Models/Client/OrderData.cs
export interface OrderData {
    id: string;
    clientId: string;
    clientName: string;
    creationDate: string;
    createdBy: string;
    notes?: string;
    items: ExtraItem[];
    payments: Payment[];
    invoices?: Invoice[];
    missingToPay?: number;
}

// ../../Appointmint.Models/Client/OrderData.cs
export interface CreateOrderData {
    clientName: string;
    items: ExtraItem[];
    payments: Payment[];
    notes?: string;
}

// ../../Appointmint.Models/Client/OrderData.cs
export interface UpdateOrderData {
    items: ExtraItem[];
    payments: Payment[];
    notes?: string;
}

// ../../Appointmint.Models/Client/TestCommunication.cs
export interface TestCommunication {
    receiverEmailOrPhoneNumber: string;
    appointment: AppointmentData;
    remindersSetting: RemindersSettingsData;
    isConfirmationRequest: boolean;
}

// ../../Appointmint.Models/Client/CashRegisterRequests.cs
export interface CreateCashRegisterReserveRequest {
    paymentType: string;
    reserveAmount: number;
    description: string;
    cashRegisterName?: string;
}

// ../../Appointmint.Models/Client/CashRegisterRequests.cs
export interface CreateCashRegisterWithdrawRequest {
    paymentType: string;
    amount: number;
    description: string;
    cashRegisterName?: string;
    targetUsername?: string;
}

// ../../Appointmint.Models/Client/CashRegisterRequests.cs
export interface CreateCashRegisterTransferRequest {
    paymentType: string;
    amount: number;
    source: CashRegisterReference;
    destination: CashRegisterReference;
    description: string;
}

// ../../Appointmint.Models/Client/CashRegisterRequests.cs
export interface CashRegisterReference {
    cashRegisterName?: string;
    username?: string;
}

// ../../Appointmint.Models/Client/TenantGrade.cs
export enum TenantGrade {
    Unspecified,
    Testing,
    Active,
}

// ../../Appointmint.Models/Client/ClientOption.cs
export interface ClientOption {
    id: string;
    name: string;
}

// ../../Appointmint.Models/Client/AIChat.cs
export interface DialogData {
    clientPhoneNumber: string;
    messages: Message[];
}

// ../../Appointmint.Models/Client/AIChat.cs
export interface Message {
    content: string;
    isAssistant: boolean;
    timestamp: string;
}

// ../../Appointmint.Models/Client/AIChat.cs
export interface DialogResponse {
    answer: string;
    usage: TokenUsage;
}

// ../../Appointmint.Models/Client/AIChat.cs
export interface TokenUsage {
    inputTokens: number;
    outputTokens: number;
}

// ../../Appointmint.Models/Client/MailerLiteFieldsResponse.cs
export interface MailerLiteFieldsResponse {
    fields: MailerLiteAvailableFieldData[];
}

// ../../Appointmint.Models/Client/Authenticate.cs
export interface Authenticate {
    username?: string;
    password?: string;
    companyId?: number;
    googleAuth?: GoogleAuthRequest;
    isClient?: boolean;
    impersonateUsername?: string;
}

// ../../Appointmint.Models/Client/TagSettings.cs
export interface TagSettings {
    color: string;
}

// ../../Appointmint.Models/Client/SelfScheduleRequest.cs
export interface SelfScheduleRequest {
    companyId: string;
    dateTime: string;
    appointmentType: string;
    room: string;
    professionalId: string;
    notes?: string;
    clientId?: string;
}

// ../../Appointmint.Models/Client/Availability.cs
export interface Availability {
    dateTime: string;
    room: string;
    professionalId: string;
}

// ../../Appointmint.Models/Client/TaxExemption.cs
export interface TaxExemption {
    id: string;
    text: string;
    law: string[];
}

// ../../Appointmint.Models/Client/ClientCommunicationsResponse.cs
export interface ClientCommunicationsResponse {
    messages: CommunicationData[];
    latestGoogleReview?: GoogleReviewSummary;
}

// ../../Appointmint.Models/Client/MailerLiteApiKeyRequest.cs
export interface MailerLiteApiKeyRequest {
    apiKey: string;
}

// ../../Appointmint.Models/Client/AppointmentConfirmationSettingsData.cs
export interface AppointmentConfirmationSettingsData {
    channels?: number[];
    hoursBeforeAppointment?: number;
    name?: string;
    message?: string;
    responses?: AppointmentConfirmationResponsesData;
}

// ../../Appointmint.Models/Client/AppointmentConfirmationSettingsData.cs
export interface AppointmentConfirmationResponsesData {
    confirm?: string[];
    reject?: string[];
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface CommunicationAttachmentData {
    id: string;
    fileName: string;
    url: string;
    contentType: string;
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface CommunicationData {
    id: string;
    timestamp: string;
    type: CommunicationType;
    direction: CommunicationDirection;
    to: string;
    from: string;
    subject: string;
    text: string;
    count: number;
    username?: string;
    name?: string;
    clientId?: string;
    clientName?: string;
    professionalId?: string;
    professionalName?: string;
    conversationId?: string;
    attachments?: CommunicationAttachmentData[];
    isRead: boolean;
    reads?: CommunicationRead[];
    status: CommunicationStatus;
    statusUpdateTimestamp?: string;
    observations?: string;
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface IncomingMessageNotification {
    id: string;
    clientId: string;
    clientName: string;
    textPreview: string;
    type: CommunicationType;
    timestamp: string;
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface IncomingMessagesSummary {
    unreadMessages: IncomingMessageNotification[];
    recentMessages: IncomingMessageNotification[];
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface CommunicationStatusUpdate {
    id: string;
    status: CommunicationStatus;
    statusUpdateTimestamp: string;
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export interface CommunicationRead {
    username: string;
    readAt: string;
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export enum CommunicationType {
    None = 0,
    SMS = 1,
    Email = 2,
    WhatsApp = 3,
    PhoneCall = 4,
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export enum CommunicationDirection {
    Sent,
    Received,
}

// ../../Appointmint.Models/Client/CommunicationData.cs
export enum CommunicationStatus {
    Pending = 0,
    Sent = 1,
    Failed = 2,
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionPlanDefinition {
    type: string;
    name: string;
    description: string;
    displayOrder: number;
    pricePerMonthBilledMonthly: number;
    pricePerMonthBilledAnnually: number;
    packIdBilledMonthly: string;
    packIdBilledAnnually: string;
    includedUsers: number;
    includedSms: number;
    includedWhatsApp: number;
    includedStorageGb: number;
    features: SubscriptionFeatureDefinition[];
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionFeatureDefinition {
    key: string;
    name: string;
    description: string;
    includedInBasic: boolean;
    includedInPro: boolean;
    includedInProPlus: boolean;
    feature?: FeatureEnum;
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionAddonDefinition {
    type: string;
    name: string;
    description: string;
    category: string;
    onlyAvailableForClientTag?: string;
    notVisibleOnUi: boolean;
    availableForMontlhyBilling: boolean;
    tiers?: SubscriptionAddonTier[];
    feature?: FeatureEnum;
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionAddonTier {
    quantity: number;
    pricePerMonth: number;
    pricePerMonthBilledMonthly?: number;
    pricePerMonthBilledAnnually?: number;
    extras?: SubscriptionExtraRef[];
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionExtraRef {
    id: string;
    quantity: number;
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionDiscount {
    name: string;
    monthlyDiscountValue: number;
    yearlyDiscountValue: number;
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionData {
    startDate: string;
    endDate: string;
    subscriptionName?: string;
    smsAllowed: number;
    emailAllowed: number;
    whatsAppAllowed: number;
    storageAllowed: number;
    hasInvoices: boolean;
    hasMintuChatbot: boolean;
    hasIntegrations: boolean;
    hasProfessionalPacks: boolean;
    usersCount: number;
    billedAnnually: boolean;
    isTrial: boolean;
    lastPaymentDate?: string;
    paymentValidityDate?: string;
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export interface SubscriptionDataResponse {
    plans: Record<string, SubscriptionPlanDefinition>;
    addons: Record<string, SubscriptionAddonDefinition>;
    currentSubscription?: SubscriptionData;
    nextSubscription?: SubscriptionData;
    discounts: SubscriptionDiscount[];
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export enum SubscriptionPlanType {
    Basic = 1,
    Pro = 2,
    ProPlus = 3,
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export enum AddonType {
    Users = 1,
    Sms = 2,
    Emails = 3,
    Billing = 4,
    Storage = 5,
    Mintu = 6,
    Integrations = 7,
    ProfessionalPacks = 8,
    WhatsApp = 9,
}

// ../../Appointmint.Models/Client/SubscriptionModels.cs
export enum AddonCategory {
    Usage = 1,
    Feature = 2,
}

// ../../Appointmint.Models/Client/CampaignChannel.cs
export enum CampaignChannel {
    Email = 0,
    WhatsApp = 1,
    SMS = 2,
}

// ../../Appointmint.Models/Client/ProfessionalAvailability.cs
export interface ProfessionalAvailability {
    from: string;
    timeSlots: TimeInterval[];
}

// ../../Appointmint.Models/Client/CompanyPublicData.cs
export interface CompanyPublicData {
    id: string;
    name: string;
    logo?: string;
    contacts: Record<string, string>;
    clientsLogin?: ClientsLoginSettingsData;
    services?: Record<string, ServiceSettings>;
    selfScheduling: SelfSchedulingPublicSettings;
}

// ../../Appointmint.Models/Client/CompanyPublicData.cs
export interface SelfSchedulingPublicSettings {
    layout: SelfSchedulingLayout;
}

// ../../Appointmint.Models/Client/CreditNoteData.cs
export interface CreditNoteData {
    originalInvoiceId: string;
    reason: string;
    branchId?: string;
    appointmentId?: string;
    packId?: string;
}

// ../../Appointmint.Models/Client/CreditNoteData.cs
export interface CreditNoteItem {
    code: string;
    quantity: number;
    rowIndex?: number;
}

// ../../Appointmint.Models/Client/CommunicationChannel.cs
export enum CommunicationChannel {
    SMS = 0,
    WhatsApp = 1,
    Email = 2,
}

// ../../Appointmint.Models/Client/TaskItemData.cs
export interface TaskItemData {
    id: string;
    type?: TaskItemType;
    status: TaskItemStatus;
    clientId?: string;
    clientName?: string;
    appointmentId?: string;
    assignedUserId?: string;
    assignedUserName?: string;
    assignedProfessionalId?: string;
    title?: string;
    dueDate: string;
    createdAt: string;
    completedAt?: string;
    notes?: string;
    dismissReason?: string;
    createdBy: string;
    clientPhoneNumber?: string;
}

// ../../Appointmint.Models/Client/TaskItemData.cs
export enum TaskItemType {
    ReminderClientFollowUp = 0,
    Reminders = 1,
    Other = 4,
}

// ../../Appointmint.Models/Client/TaskItemData.cs
export enum TaskItemStatus {
    Pending = 0,
    Completed = 1,
    Dismissed = 2,
}

// ../../Appointmint.Models/Client/Report.cs
export interface Report {
    id: string;
    templateId: string;
    name: string;
    version: number;
    visibleForAdministratives?: boolean;
    creationDate: string;
    createdBy: string;
    lastChangeDt?: string;
    lastChangeBy?: string;
    clientId: string;
    professionalId?: string;
    values: Record<string, string>;
    clientName?: string;
    professionalName?: string;
    professionalColor?: string;
    share?: ShareOptions;
}

// ../../Appointmint.Models/Client/Payment.cs
export interface Payment {
    id?: string;
    username?: string;
    status?: PaymentStatus;
    date: string;
    value: number;
    type: string;
    amount?: number;
    vat?: number;
    notes?: string;
    comissionWithoutVat?: number;
    cashRegisterName?: string;
}

// ../../Appointmint.Models/Client/Payment.cs
export enum PaymentStatus {
    None,
    Manual,
    InProgress,
    Success,
    Failed,
}

// ../../Appointmint.Models/Client/IntegrationSyncRequest.cs
export interface IntegrationSyncRequest {
    type: IntegrationType;
    createIfNotExists: boolean;
}

// ../../Appointmint.Models/Client/CampaignRecipientEstimate.cs
export interface CampaignRecipientEstimate {
    totalIncluded: number;
    totalExcluded: number;
    exclusionBreakdown: CampaignRecipientExclusionBreakdown;
    samples: CampaignRecipientSample[];
    requiresPayment: boolean;
    pricingTotal: number;
    pricingSubtotal: number;
    pricingTax: number;
    pricingCurrency: string;
    pricingModel: string;
}

// ../../Appointmint.Models/Client/CampaignRecipientEstimate.cs
export interface CampaignRecipientExclusionBreakdown {
    noMarketingConsent: number;
    marketingConsentNotSubmitted: number;
    missingContactInfo: number;
    invalidContactInfo: number;
    duplicateAddress: number;
    manuallyExcluded: number;
}

// ../../Appointmint.Models/Client/CampaignRecipientEstimate.cs
export interface CampaignRecipientSample {
    clientId?: string;
    clientName?: string;
    email: string;
}

// ../../Appointmint.Models/Client/CommunicationQuotas.cs
export interface CommunicationQuotas {
    smsUsed: number;
    smsAllowed: number;
    smsLeft: number;
    smsQuotaExhaustedOn?: string;
    whatsAppUsed: number;
    whatsAppAllowed: number;
    whatsAppLeft: number;
    whatsAppQuotaExhaustedOn?: string;
}

// ../../Appointmint.Models/Client/MarkInvoicePaidData.cs
export interface MarkInvoicePaidData {
    paymentMethod: string;
    paymentDate: string;
    amount: number;
    sendEmail?: boolean;
    branchId?: string;
    invoiceIds?: string[];
    appointmentId?: string;
    packId?: string;
    cashRegisterName?: string;
}

// ../../Appointmint.Models/Client/PermissionEnum.cs
export enum PermissionEnum {
    PricesAccess,
    PricesManagement,
    ProfessionalsManagement,
    ClientsAccess,
    ClientsReports,
    ClientsManagement,
    AppointmentsAccess,
    AppointmentsManagement,
    Administrator,
    CampaignsManagement,
    Analytics,
    MyClientsManagement,
    ClientsChat,
    DoNotAllowSkipAvailability,
    DoNotAllowToConfirmAppointments,
    DoNotShowPrices,
    DoNotAllowToCancelInvoices,
    DoNotAllowToEmitInvoices,
    DoNotCreateOrEditAppointments,
}

// ../../Appointmint.Models/Client/WaitingListItem.cs
export interface WaitingListItem {
    clientId: string;
    clientName?: string;
    professionalId?: string;
    professionalName?: string;
    service: string;
    notes?: string;
    addedAt: string;
    addedByUsername?: string;
    addedByName?: string;
    limitDate?: string;
    priority: WaitingListPriority;
}

// ../../Appointmint.Models/Client/PrescriptionData.cs
export interface PrescriptionData {
    id: string;
    clientId: string;
    clientName?: string;
    professionalId: string;
    professionalName?: string;
    numeroReceitaLocal: string;
    numeroReceita?: string;
    pinReceita?: string;
    pinDireitoOpcao?: string;
    dataReceita?: string;
    state: PrescriptionState;
    spmsResultCode?: string;
    spmsResultMessage?: string;
    lines: PrescriptionLineData[];
    creationDate: string;
}

// ../../Appointmint.Models/Client/PrescriptionData.cs
export interface PrescriptionLineData {
    numeroLinha: number;
    cnPem: string;
    substanciaActiva: string;
    formaFarmaceutica: string;
    embalagem: string;
    dosagem: string;
    quantidade: number;
    instrucoes?: string;
}

// ../../Appointmint.Models/Client/PrescriptionData.cs
export interface PrescriptionsSettingsData {
    spmsLocalPrescricaoCodigo?: string;
    spmsSubsistemaEntidadeCodigo?: string;
    spmsSubsistemaEntidadeDominio?: string;
    spmsSubsistemaEntidadePais?: string;
}

// ../../Appointmint.Models/Client/PrescriptionData.cs
export interface MedicineSearchResultData {
    designacao: string;
    cnPem: string;
    substanciaActiva: string;
    formaFarmaceutica: string;
    dosagem: string;
    embalagem?: string;
    titularAim?: string;
}

// ../../Appointmint.Models/Client/BranchSettings.cs
export interface BranchSettings {
    displayName: string;
    rooms: Record<string, RoomSettings>;
    allowAppointmentsWithoutRoom?: boolean;
    googleReviewUrl?: string;
}

// ../../Appointmint.Models/Client/RecurringInfo.cs
export interface RecurringInfo {
    groupId?: string;
    frequency: RecurrenceFrequency;
    period: number;
    endDate: string;
    weekDays: DayOfWeekEnum[];
}

// ../../Appointmint.Models/Client/TestGoogleReviewRequest.cs
export interface TestGoogleReviewRequest {
    receiverEmailOrPhoneNumber: string;
    smsTemplate?: string;
    emailBodyText?: string;
    branchId?: string;
}

// ../../Appointmint.Models/Client/ReportFieldFlag.cs
export enum ReportFieldFlag {
    ReadOnly,
    Block,
    HideLabel,
    Centered,
    AlignRight,
}

// ../../Appointmint.Models/Client/AppointmentUpdate.cs
export interface AppointmentUpdate {
    data: AppointmentData;
    changeReason?: AppointmentChangeReason;
}

// ../../Appointmint.Models/Client/ProfessionalPayment.cs
export interface ProfessionalPayment {
    period?: DateInterval;
    type: ProfessionalPaymentType;
    value?: number;
    profileId?: string;
}

// ../../Appointmint.Models/Client/CampaignOrder.cs
export interface CampaignOrder {
    id: string;
    campaignId: string;
    status: CampaignOrderStatus;
    recipientCountSnapshot: number;
    pricingModel: string;
    subtotal: number;
    tax: number;
    total: number;
    currency: string;
    checkoutId?: string;
    expiresAt?: string;
    createdBy: string;
    paidAt?: string;
    processId?: string;
    creationDate: string;
}

// ../../Appointmint.Models/Client/CampaignOrder.cs
export enum CampaignOrderStatus {
    Created,
    CheckoutStarted,
    Paid,
    Expired,
    Cancelled,
    Refunded,
}

// ../../Appointmint.Models/Client/Invoice.cs
export interface Invoice extends InvoiceData {
    id: string;
    number?: string;
    state: InvoiceState;
    total: number;
    relatedDocuments: RelatedDoc[];
    sentByEmail?: boolean;
    sentByEmailAt?: string;
}

// ../../Appointmint.Models/Client/Invoice.cs
export interface RelatedDoc {
    id: string;
    type: InvoiceDocumentType;
    number?: string;
    amount?: string;
    status?: string;
}

// ../../Appointmint.Models/Client/AuthenticatorSetupResponse.cs
export interface AuthenticatorSetupResponse {
    /**
     * The secret key in Base32 format for manual entry
     */
    secret: string;
    /**
     * The QR code data URL (data:image/png;base64,...)
     */
    qrCodeDataUrl: string;
    /**
     * The otpauth:// URI for the authenticator app
     */
    otpAuthUri: string;
    /**
     * The session ID to use when confirming the setup
     */
    sessionId: string;
}

// ../../Appointmint.Models/Client/IdentitySource.cs
export interface IdentitySource {
}

// ../../Appointmint.Models/Client/SignUpRequest.cs
export interface SignUpServiceData {
    name?: string;
    defaultPrice?: number;
}

// ../../Appointmint.Models/Client/SignUpRequest.cs
export interface SignUpProfessionalData {
    name?: string;
    email?: string;
}

// ../../Appointmint.Models/Client/SignUpRequest.cs
export interface SignUpRequest {
    company?: string;
    username?: string;
    password?: string;
    referralCode?: string;
    googleAuth?: GoogleAuthRequest;
    servicesWithPrice?: SignUpServiceData[];
    rooms?: string[];
    professionals?: SignUpProfessionalData[];
    notifyProfessionalsByEmail?: boolean;
    acceptedTermsAndConditions?: boolean;
    acceptedPrivacyPolicy?: boolean;
    marketingAcceptedByEmail?: boolean;
    consentCulture?: string;
    countryCode?: string;
    partnerCode?: string;
    partnerId?: string;
}

// ../../Appointmint.Models/Client/StockData.cs
export interface StockData {
    id: string;
    name: string;
    category: string;
    defaultPrice: number;
    amount: number;
}

// ../../Appointmint.Models/Client/IntegrationInvoiceXpress.cs
export interface IntegrationInvoiceXpress {
    apiKey?: string;
    accountName?: string;
}

// ../../Appointmint.Models/Client/ExtraData.cs
export interface ExtraData {
    id: string;
    name: string;
    category: string;
    creationDate: string;
    appointmentTypes: string[];
    priceForClient: number;
    professionalPart: number;
    stockItems: StockItemUsage[];
    invoiceItemDetails?: InvoiceItemDetails;
}

// ../../Appointmint.Models/Client/ExtraItem.cs
export interface ExtraItem {
    id: string;
    name: string;
    value: number;
    vat: number;
    quantity: number;
}

// ../../Appointmint.Models/Client/CreateTaskItemData.cs
export interface CreateTaskItemData {
    type?: TaskItemType;
    clientId?: string;
    appointmentId?: string;
    assignedUserId?: string;
    title?: string;
    notes?: string;
    branchId?: string;
    dueDate: string;
}

// ../../Appointmint.Models/Client/CampaignReduced.cs
export interface CampaignReduced {
    id: string;
    name: string;
    lastUpdateDt: string;
    status: CampaignStatus;
    channel: CampaignChannel;
    createdBy?: string;
    createdByName?: string;
    lastTriggeredAt?: string;
    lastRecipientEstimate?: number;
}

// ../../Appointmint.Models/Client/Campaign.cs
export interface Campaign extends CampaignBase {
    id: string;
    noOfRecipients: number;
    status: CampaignStatus;
    lastTriggeredAt?: string;
    lastTriggeredBy?: string;
    lastRecipientEstimate?: number;
    lastRecipientEstimateAt?: string;
    archivedAt?: string;
    archivedBy?: string;
    reTriggerCooldownDays: number;
}

// ../../Appointmint.Models/Client/ChangeEntityLog.cs
export interface ChangeEntityLog extends ChangeLog {
    id: string;
    entityId: string;
    entityType: EntityType;
    entityDescription: string;
}

// ../../Appointmint.Models/Client/ChangeEntityLog.cs
export enum EntityType {
    Client,
    Professional,
    User,
    Appointment,
    Settings,
    Pack,
    Report,
    FileReference,
    Campaign,
    Expense,
    Stock,
    SettingsGeneral,
    SettingsPrices,
    SettingsExpenses,
    SettingsExtras,
    SettingsEmail,
    SettingsReports,
    SettingsPacks,
    SettingsReminders,
    SettingsPayments,
    SettingsSet,
    SettingsClientsLogin,
    Order,
}

// ../../Appointmint.Models/Client/ChangeLog.cs
export interface ChangeLog {
    timestamp: string;
    username: string;
    changeKind: ChangeKind;
    details: string[];
}

// ../../Appointmint.Models/Client/DateInterval.cs
export interface DateInterval {
    from: string;
    to: string;
    description: string;
    recurring?: RecurringConfig;
}

// ../../Appointmint.Models/Client/SetMintuPauseRequest.cs
export interface SetMintuPauseRequest {
    pauseHours?: number;
}

// ../../Appointmint.Models/Client/CampaignTest.cs
export interface CampaignTest {
    to: string;
    subject: string;
    content: string;
}

// ../../Appointmint.Models/Client/CampaignTest.cs
export interface CampaignTestEmailRequest {
    to: string;
}

// ../../Appointmint.Models/Client/CampaignTest.cs
export interface CreateCampaignOrderRequest {
    recipientCount: number;
}

// ../../Appointmint.Models/Client/UpdateStockQuantity.cs
export interface UpdateStockQuantity {
    quantity: number;
    reason: string;
}

// ../../Appointmint.Models/Client/ClientTagRuleType.cs
export enum ClientTagRuleType {
    Active = 0,
    AtRisk = 1,
    Inactive = 2,
    Debtor = 3,
}

// ../../Appointmint.Models/Client/ReportFieldType.cs
export enum ReportFieldType {
    Text,
    MultilineText,
    IntNumber,
    FloatNumber,
    Date,
    MultiOptions,
    Scale,
    ImageMarker,
    Table,
    Separator,
    Signature,
    BlankSpace,
}

// ../../Appointmint.Models/Client/UpdateUserData.cs
export interface UpdateUserData {
    id: string;
    password?: string;
    email?: string;
    permissions?: PermissionEnum[];
    professionalId?: string;
    name?: string;
    photoFileId?: string;
    mfa?: MfaMethodEnum;
    mfaPhoneNumber?: string;
    managerId?: string;
    branchIds?: string[];
    defaultCashRegisterName?: string;
}

// ../../Appointmint.Models/Client/DocumentSettings.cs
export interface DocumentSettings {
    id: string;
    name: string;
    icon?: string;
    colorCode?: string;
    availableForAllAppointmentTypes?: boolean;
    availableForAppointmentTypes: string[];
    availableForAppointmentsManager: boolean;
}

// ../../Appointmint.Models/Client/ClientPropertySettings.cs
export interface ClientPropertySettings {
    description?: string;
    type: string;
    width?: string;
    unit?: string;
    professionalCanView?: boolean;
    professionalCanEdit?: boolean;
    clientCanView?: boolean;
    clientCanEdit?: boolean;
}

// ../../Appointmint.Models/Client/WaitingListHistoryData.cs
export interface WaitingListHistoryData {
    id: string;
    clientId: string;
    clientName?: string;
    professionalId?: string;
    professionalName?: string;
    service: string;
    notes?: string;
    priority: WaitingListPriority;
    limitDate?: string;
    addedAt: string;
    removedAt: string;
    durationDays: number;
    removedByUsername?: string;
    removalReason: WaitingListRemovalReason;
}

// ../../Appointmint.Models/Client/SettingsData.cs
export interface SettingsData {
    appVersion?: string;
    general: GeneralSettingsData;
    consentManagement: ConsentManagementSettingsData;
    extras: ExtrasSettingsData;
    reports: ReportsSettingsData;
    reminders: RemindersSettingsData;
    taskManagement?: TaskManagementSettingsData;
    alerts: AlertsSettingsData;
    payments: PaymentsSettingsData;
    clientsLogin: ClientsLoginSettingsData;
    integrations: IntegrationsSettingsData;
    invoices: InvoiceSettingsData;
    cashRegisterSettings: CashRegisterSettings;
    waitingListSettings: WaitingListSettingsData;
    aiPoweredRequests: AiPoweredRequestsData;
}

// ../../Appointmint.Models/Client/SettingsData.cs
export interface AiPoweredRequestsData {
    reports: AiPoweredRequestsReportsData;
    surveys: AiPoweredRequestsSurveysData;
}

// ../../Appointmint.Models/Client/SettingsData.cs
export interface AiPoweredRequestsReportsData {
    totalAvailableQuota: number;
    left: number;
    lastResetUtc: string;
    quotaExhaustedOnUtc?: string;
}

// ../../Appointmint.Models/Client/SettingsData.cs
export interface AiPoweredRequestsSurveysData {
    totalAvailableQuota: number;
    left: number;
    lastResetUtc: string;
    quotaExhaustedOnUtc?: string;
}

// ../../Appointmint.Models/Client/RecurrenceFrequency.cs
export enum RecurrenceFrequency {
    Daily = 0,
    Weekly = 1,
    Monthly = 2,
}

// ../../Appointmint.Models/Client/GeneralSettingsData.cs
export interface GeneralSettingsData {
    companyName?: string;
    companyImageUrl?: string;
    openningHour: string;
    closingHour: string;
    countryCode: string;
    timeZone: string;
    contacts: Record<string, string>;
    services: Record<string, ServiceSettings>;
    rooms: Record<string, RoomSettings>;
    branches?: Record<string, BranchSettings>;
    clientTags: Record<string, TagSettings>;
    clientIntelligentCategorization?: ClientIntelligentCategorizationSettings;
    paymentMethods: string[];
    clientOrigins: string[];
    clientSubsystems: string[];
    expenseTypes: string[];
    expenseEntities: string[];
    stockCategories: string[];
    appointmentChangeReasons: string[];
    doNotUseAppointmentConfirmation?: boolean;
    disableMissingPaymentNotifications?: boolean;
    allowAppointmentsWithoutRoom?: boolean;
    professionalCanEditAppointmentUpToMinutes?: number;
    useClientFileNumber?: boolean;
    prePayment?: ServicePrePaymentSettings;
    absences?: DateInterval[];
    googleReviewUrl?: string;
    clientProperties?: Record<string, ClientPropertySettings>;
    fixedClientProperties?: Record<string, FixedClientPropertySettings>;
}

// ../../Appointmint.Models/Client/PaymentsSettingsData.cs
export interface PaymentsSettingsData {
    profiles?: PaymentProfile[];
    easypay?: EasypaySettingsData;
}

// ../../Appointmint.Models/Client/EasypaySettingsData.cs
export interface EasypaySettingsData {
    testMode: boolean;
    accountId?: string;
    apiKey?: string;
    paymentAccountId?: string;
}

// ../../Appointmint.Models/Client/PrescriptionState.cs
export enum PrescriptionState {
    Pending,
    Registered,
    Error,
    Cancelled,
}

// ../../Appointmint.Models/Client/GoogleSettingsData.cs
export interface GoogleSettingsData {
    googleReview?: GoogleReviewSettingsData;
}

// ../../Appointmint.Models/Client/RequestGoogleReviewRequest.cs
export interface RequestGoogleReviewRequest {
    channel?: CommunicationType;
    branchId?: string;
}

// ../../Appointmint.Models/Client/AppointmentChangeType.cs
export enum AppointmentChangeType {
    Deleted = 0,
    DateChanged = 1,
    ClientChanged = 2,
}

// ../../Appointmint.Models/Client/ProfessionalPaymentType.cs
export enum ProfessionalPaymentType {
    None,
    Percentage,
    PercentageEffective,
    PerHour,
    Fixed,
    Profile,
}

// ../../Appointmint.Models/Client/PaymentData.cs
export interface PaymentData {
    id: string;
    date: string;
    value: number;
    type: string;
    origin: PaymentOrigin;
    originId: string;
    clientName: string;
    clientId?: string;
    status: PaymentStatus;
    vat?: number;
    comissionWithoutVat?: number;
    cashRegisterName?: string;
    username?: string;
}

// ../../Appointmint.Models/Client/PaymentData.cs
export enum PaymentOrigin {
    None,
    Appointment,
    Pack,
    Order,
}

// ../../Appointmint.Models/Client/SendConsentApprovalRequest.cs
export interface SendConsentApprovalRequest {
    channel: ConsentDeliveryChannel;
}

// ../../Appointmint.Models/Client/InvoiceAppointmentsData.cs
export interface InvoiceManyAppointmentsData extends InvoiceData {
    appointmentIds: string[];
}

// ../../Appointmint.Models/Client/CashRegisterSettings.cs
export interface CashRegisterSettings {
    enabled: boolean;
    allPaymentTypes: boolean;
    paymentTypes: string[];
    nonAdminApprovers: string[];
    sharedRegisterNames: string[];
    cashPaymentType?: string;
}

// ../../Appointmint.Models/Client/CreateUserData.cs
export interface CreateUserData {
    username: string;
    password?: string;
    email?: string;
    permissions: PermissionEnum[];
    professionalId?: string;
    name?: string;
    photoFileId?: string;
    mfa?: MfaMethodEnum;
    mfaPhoneNumber?: string;
    branchIds?: string[];
}

// ../../Appointmint.Models/Client/BulkAppointmentCreationResult.cs
export interface BulkAppointmentCreationResult {
    results: BulkAppointmentCreationResultItem[];
}

// ../../Appointmint.Models/Client/BulkAppointmentCreationResult.cs
export interface BulkAppointmentCreationResultItem {
    id?: string;
    date: string;
    start: string;
    errorMessage?: string;
}

// ../../Appointmint.Models/Client/StockHistoryType.cs
export enum StockHistoryType {
    Appointment,
    Extra,
    Expense,
    Manual,
}

// ../../Appointmint.Models/Client/MailerLiteFieldMappingData.cs
export interface MailerLiteFieldMappingData {
    mailerLiteFieldKey: string;
    mailerLiteFieldName: string;
    appointmintFieldKey?: string;
    clientTagPrefix?: string;
}

// ../../Appointmint.Models/Client/ClientPack.cs
export interface ClientPack {
    id: string;
    packTemplateId: string;
    packName: string;
    packVersion: number;
    appointmentTypes: string[];
    price: number;
    vat: number;
    notes?: string;
    totalMinutes?: number;
    totalCredits?: number;
    totalMinutesStillAvailable?: number;
    totalCreditsStillAvailable?: number;
    clientId: string;
    clientName: string;
    creationDate?: string;
    createdBy: string;
    validityTo?: string;
    validityFrom?: string;
    payments?: Payment[];
    invoices?: Invoice[];
    appointments?: string[];
    clientIsProfessional?: boolean;
    isSubscription?: boolean;
    extras: ExtraItem[];
}

// ../../Appointmint.Models/Client/UserData.cs
export interface UserData {
    id: string;
    username: string;
    email?: string;
    emailConfirmed?: boolean;
    permissions: PermissionEnum[];
    professionalId?: string;
    name?: string;
    photoFileId?: string;
    photoUrl?: string;
    identitySource?: string;
    managerId?: string;
    branchIds?: string[];
    defaultCashRegisterName?: string;
    preferences?: UserPreferencesData;
    mfa: MfaMethodEnum;
    mfaPhoneNumber?: string;
    mfaAuthenticatorConfigured?: boolean;
}

// ../../Appointmint.Models/Client/ExtrasSettingsData.cs
export interface ExtrasSettingsData {
    extras: ExtraData[];
}

// ../../Appointmint.Models/Client/GenerateReportTemplateRequest.cs
export interface GenerateReportTemplateRequest {
    prompt: string;
    template?: ReportSettings;
}

// ../../Appointmint.Models/Client/TimeInterval.cs
export interface TimeInterval {
    dayOfWeek: DayOfWeekEnum;
    start: string;
    end: string;
}

// ../../Appointmint.Models/Client/GenerateSurveyRequest.cs
export interface GenerateSurveyRequest {
    prompt: string;
    template?: SurveySettingsData;
}

// ../../Appointmint.Models/Client/StockItemUsage.cs
export interface StockItemUsage {
    stockId: string;
    quantity: number;
}

// ../../Appointmint.Models/Client/PackSettings.cs
export interface PackSettings {
    id?: string;
    templateId?: string;
    name: string;
    version: number;
    disabled?: boolean;
    creationDate: string;
    appointmentTypes: string[];
    price: number;
    validityDays?: number;
    totalMinutes?: number;
    appointmentTypesCredits?: Record<string, number>;
    totalCredits?: number;
    subscriptionUnit?: RecurrenceFrequency;
    subscriptionPeriod?: number;
    invoiceItemDetails?: InvoiceItemDetails;
}

// ../../Appointmint.Models/Client/ReportTemplateData.cs
export interface ReportTemplateData {
    id: string;
    templateId: string;
    name: string;
    version: number;
    disabled?: boolean;
    icon?: string;
    colorCode?: string;
    hideHeader?: boolean;
    availableForAllAppointmentTypes?: boolean;
    editableAfterCreationMinutes?: number;
    creationDate: string;
    availableForAppointmentTypes: string[];
    mandatoryForAppointmentTypes: string[];
    availableForAppointmentsManager: boolean;
    shareWithClient?: boolean;
    fields: ReportFieldDefinition[];
    showReportInline?: boolean;
    printZoom?: number;
    categoryKey?: string;
}

// ../../Appointmint.Models/Client/CatalogTemplateData.cs
export interface CatalogTemplateData {
    key: string;
    categoryKey: string;
    name: string;
    description: string;
    icon: string;
    colorCode: string;
    availableForAllAppointmentTypes: boolean;
    editableAfterCreationMinutes: number;
    availableForAppointmentsManager: boolean;
    showReportInline: boolean;
    hideHeader: boolean;
    printZoom: number;
    fields: ReportFieldDefinition[];
}

// ../../Appointmint.Models/Client/CatalogCategoryData.cs
export interface CatalogCategoryData {
    key: string;
    name: string;
    icon: string;
    colorCode: string;
    templates: CatalogTemplateData[];
}

// ../../Appointmint.Models/Client/ClientsFollowUpSettingsData.cs
export interface ClientsFollowUpSettingsData {
    enabled?: boolean;
    daysAfterFirstAppointment?: number;
    communicationChannel?: CommunicationChannel;
    communicationTemplate?: string;
    communicationSubjectTemplate?: string;
}

// ../../Appointmint.Models/Client/UpdateTaskItemStatusData.cs
export interface UpdateTaskItemStatusData {
    status: TaskItemStatus;
    notes?: string;
    dismissReason?: string;
}

// ../../Appointmint.Models/Client/SurveySettings.cs
export interface SurveyFieldDefinition {
    label: string;
    type: SurveyFieldType;
    isMandatory: boolean;
    options?: string[];
    scaleMin?: number;
    scaleMax?: number;
}

// ../../Appointmint.Models/Client/SurveySettings.cs
export interface SurveySettingsData {
    id: string;
    name: string;
    description?: string;
    creationDate: string;
    emailSubject?: string;
    emailHtml?: string;
    emailFromName?: string;
    emailFrom?: string;
    fields: SurveyFieldDefinition[];
    automaticForServiceTypeIds?: string[];
    isDefaultAutomaticSurvey?: boolean;
    whatsAppEnabled?: boolean;
    autoCreateClientIfNotExists?: boolean;
    totalSent: number;
    totalResponses: number;
}

// ../../Appointmint.Models/Client/SurveySettings.cs
export enum SurveyFieldType {
    MultilineText = 0,
    IntNumber = 1,
    MultiOptions = 2,
    Scale = 3,
    Date = 4,
    SingleLineText = 5,
}

// ../../Appointmint.Models/Client/GoogleReviewAnalyticsData.cs
export interface GoogleReviewAnalyticsData {
    totalSent: number;
    totalOpened: number;
    totalSubmitted: number;
    totalClickedGoogle: number;
    averageStars?: number;
    starDistribution: Record<number, number>;
    positiveComments: string[];
    improvementNotes: string[];
    byProfessional: GoogleReviewDimensionMetric[];
    byBranch: GoogleReviewDimensionMetric[];
    byChannel: GoogleReviewDimensionMetric[];
}

// ../../Appointmint.Models/Client/GoogleReviewAnalyticsData.cs
export interface GoogleReviewDimensionMetric {
    id: string;
    label: string;
    totalSent: number;
    totalSubmitted: number;
    averageStars?: number;
}

// ../../Appointmint.Models/Client/AssignInvoiceData.cs
export enum InvoiceAssignEntityType {
    Appointment = 0,
    Pack = 1,
    Order = 2,
}

export interface AssignInvoiceData {
    entityType: InvoiceAssignEntityType;
    entityIds: string[];
}

export type ClientOrder = OrderData;
